import React from 'react';
import i18n from '../../i18n';
import Fields from '../editors';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    AlertTitle,
    Alert
} from '@mui/material';
import { adaptedName } from '../../utils/dataHelpers';
import { AppDataContext } from '../../context/Context';
import { DialogSaveAdapter } from '../../adapter/DialogSaveAdapter';
import { isFieldVisible } from '../../utils/fields';
import {
    getVisibleFieldSet,
    isFieldInVisibleSet
} from '../../utils/isFieldVisible';
import PropTypes from 'prop-types';
import { LoadingButton } from '@mui/lab';
import TemplateButton from '../../components/templates/TemplateButton';
import { TemplateService } from '../../services/templatesServise/TemplateService';

export default class DialogAbstract extends React.Component {
    onClose = () => {};
    onSave = (data) => {};

    Name;
    Head;
    Server;
    unsubscribe = () => {};

    constructor(props) {
        super(props);
        this.state = {
            dialogOpen: props.dialogOpen || false,
            dialogData: props.dialogData || {},
            loading: false,
            templateLockedFields: new Set(),
            permissions: {} // Initialize permissions in state
        };

        this.Name = props.Name;
        this.Head = props.Head;
        this.Server = props.Server;

        this.onClose = props.onClose;
        this.onSave = props.onSave;
    }

    static getDerivedStateFromProps(props, state) {
        if (props.dialogOpen !== state.dialogOpen) {
            return {
                dialogOpen: props.dialogOpen,
                dialogData: props.dialogData,
                loading: false
            };
        }
        return null;
    }
    static contextType = AppDataContext;

    componentDidMount() {
        this.setState({ permissions: this.context.getState().permissions });
        this.unsubscribe = this.context.subscribe(() => {
            this.setState({ permissions: this.context.getState().permissions });
        });
    }

    componentWillUnmount() {
        this.unsubscribe();
    }

    hasPermissions = () => {
        return !!Object.keys(this.state.permissions).length;
    };

    allowedPermission = (action, controller = this.Name) => {
        const adaptedController = adaptedName(controller);
        const { permissions } = this.state;

        const permission = this.hasPermissions()
            ? permissions?.[adaptedController]?.[action]
            : '';

        if (permission === 'allow_all') return true;
        if (permission === 'deny') return false;
    };

    handleDialogSave = async (event, name) => {
        const data = this.state.dialogData;
        const adapter = new DialogSaveAdapter(data);
        const adaptedData = adapter.newData;
        this.setState({ loading: true });
        const mode = adaptedData.id ? 'edit' : 'add';
        this.setState({ loading: true });
        try {
            await this.onSave(adaptedData, mode);
        } finally {
            this.setState({ loading: false });
        }
    };

    handleDialogCancel = (event, name) => {
        this.handleDialogClose();
    };

    handleDialogClose = (event, name) => {
        // what to do if we have data changed and dialog is closing
        if (!this.state.loading) {
            this.setState({ dialogData: {} });
            this.onClose();
        }
    };

    handleDialogValueChanged(changedId, value) {
        this.setState((prev) => {
            const oldData = { ...(prev.dialogData || {}) };
            const newData = { ...oldData, [changedId]: value };

            this.Head.forEach((field) => {
                if (typeof field.showIfDialog === 'function') {
                    const wasVisible = !!field.showIfDialog(oldData);
                    const nowVisible = !!field.showIfDialog(newData);
                    if (wasVisible && !nowVisible) {
                        newData[field.id] = null;
                    }
                }
            });

            return { dialogData: newData };
        });
    }


    splitContent = (content) => {
        const middleIndex = Math.ceil(content.length / 2);
        return [content.slice(0, middleIndex), content.slice(middleIndex)];
    };

    getPermissionFilterFields = (mode) => {
        let editorFields;
        if (mode === 'add') {
            editorFields = this.Head.filter(
                (f) => !f.disableEditor || (f.disableEditor && f.displayAdd)
            ).filter((f) => !f.hideOnAdd);
        } else {
            editorFields = this.Head.filter(
                (f) => !f.disableEditor || f.displayOnEditForReadOnly
            );
        }
        return editorFields.filter((f) =>
            f.from ? this.allowedPermission('read', f.from) : true
        );
    };


    renderField = (field, data, mode) => {
        const Field = Fields[field.cell] || Fields.Text; // fallback to Text
        const fieldValue = data[field.id];
        const isLocked =
            this.state.templateLockedFields &&
            this.state.templateLockedFields.has(field.id);
        return (
            <Field
                key={field.id}
                field={{ ...field, disabledByTemplate: isLocked }}
                disabled={Boolean(isLocked) || field.disabled === true}
                data={data}
                value={fieldValue}
                mode={mode}
                context={this.context}
                onChange={(id, value) => {
                    if (!isLocked) {
                        this.handleDialogValueChanged(id, value);
                    }
                }}
            />
        );
    };

    renderFields = (fields, mode) =>
        fields.map((field) =>
            this.renderField(field, this.state.dialogData || {}, mode)
        );

    renderGridContent = (content, mode) =>
        content.map((field) =>
            this.renderField(field, this.state.dialogData || {}, mode)
        );

    computeLockedFieldsFromTemplate = (tplData) => {
        const locks = new Set();
        (this.Head || []).forEach((f) => {
            if (!f || !f.id) return;

            const shouldLock =
                typeof f.lockOnTemplate === 'boolean' ? f.lockOnTemplate : true;

            if (!shouldLock) return;

            if (Object.prototype.hasOwnProperty.call(tplData, f.id)) {
                const v = tplData[f.id];
                if (v !== undefined) {
                    locks.add(f.id);
                }
            }
        });
        return locks;
    };


    render() {
        const { dialogData, dialogOpen, loading } = this.state;
        const { role, error } = this.props;

        const mode = dialogData?.id ? 'edit' : 'add';
        const editMode = mode === 'edit';

        const visibleFieldSet = getVisibleFieldSet(role);
        const permissionFilterFields = this.getPermissionFilterFields(mode);

        const roleFilteredFields = permissionFilterFields
            // Check if field is visible based on role and configuration
            .filter((field) => isFieldInVisibleSet(field, visibleFieldSet))
            .filter((field) =>
                // Check if field visibility condition is met
                isFieldVisible(field, dialogData || {}, 'dialog')
            );

        const [firstContent, secondContent] =
            this.splitContent(roleFilteredFields);

        if (!this.hasPermissions() || !dialogData) {
            return null;
        }

        return (
            <>
                <Dialog open={dialogOpen} onClose={this.handleDialogClose}>
                    <DialogTitle>
                        {editMode
                            ? i18n.t(`pages.${this.Name}.edit`)
                            : i18n.t(`pages.${this.Name}.add`)}
                    </DialogTitle>
                    <DialogContent>
                        {error ? (
                            <Alert severity="error">
                                <AlertTitle>
                                    {i18n.t('errors.title')}
                                </AlertTitle>
                                <ul>
                                    {error.map((err, index) => (
                                        <li key={index}>{err}</li>
                                    ))}
                                </ul>
                            </Alert>
                        ) : null}
                        {roleFilteredFields.length <= 9 ? (
                            this.renderFields(roleFilteredFields, mode)
                        ) : (
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    {this.renderGridContent(firstContent, mode)}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {this.renderGridContent(
                                        secondContent,
                                        mode
                                    )}
                                </Grid>
                            </Grid>
                        )}
                        {this.props.showTemplateButton && mode === 'add' && (
                            <TemplateButton
                                label={i18n.t('buttons.fillFromTemplate')}
                                loadTemplates={this.props.loadTemplates}
                                fieldMapping={this.props.templateMapping}
                                disabled={loading}
                                onApply={(tpl) => {
                                    this.setState((prev) => {
                                        const merged = TemplateService.applyTemplate(
                                            prev.dialogData,
                                            tpl.data,
                                            this.Head
                                        );
                                        const locks = this.computeLockedFieldsFromTemplate(tpl.data);
                                        return {
                                            dialogData: merged,
                                            templateLockedFields: locks
                                        };
                                    });
                                }}
                            />
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={this.handleDialogCancel}
                            disabled={loading}
                        >
                            {i18n.t('buttons.cancel')}
                        </Button>
                        <LoadingButton
                            loading={loading}
                            onClick={(event) => this.handleDialogSave(event)}
                        >
                            {i18n.t('buttons.save')}
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </>
        );
    }
}
DialogAbstract.propTypes = {
    dialogOpen: PropTypes.bool.isRequired,
    dialogData: PropTypes.object || null,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    Name: PropTypes.string,
    Head: PropTypes.arrayOf(PropTypes.object).isRequired,
    Server: PropTypes.object,
    role: PropTypes.string.isRequired
};
